import type { WorkflowContainerStep, WorkflowEntry } from '@agent-platform/shared';

/**
 * The canvas editing model. Workflows run their entries in order, so the canvas keeps every
 * top-level entry in one array and marks where unconnected runs ("segments") begin:
 *
 *   graph  = [a, b, c, x, y]   breaks = ['x']
 *   → Input → a → b → c → Output      (the main chain: what is saved and runs)
 *     x → y                            (linked to each other, not to the workflow)
 *
 * Linking, unlinking and deleting on the canvas are operations on these segments.
 */
export interface CanvasModel {
  graph: WorkflowEntry[];
  /** Ids that start an unconnected segment. */
  breaks: string[];
}

export const CANVAS_INPUT = '__input';
export const CANVAS_OUTPUT = '__output';

/** The main chain first (possibly empty), then each unconnected segment. */
export function segments(model: CanvasModel): WorkflowEntry[][] {
  const breaks = new Set(model.breaks);
  const out: WorkflowEntry[][] = [[]];
  for (const entry of model.graph) {
    if (breaks.has(entry.id)) out.push([entry]);
    else out.at(-1)!.push(entry);
  }
  return out;
}

function fromSegments(parts: WorkflowEntry[][]): CanvasModel {
  const [main = [], ...rest] = parts;
  const loose = rest.filter((s) => s.length > 0);
  return { graph: [...main, ...loose.flat()], breaks: loose.map((s) => s[0]!.id) };
}

/** The entries that are saved and run. */
export const mainChain = (model: CanvasModel) => segments(model)[0]!;
export const looseEntries = (model: CanvasModel) => segments(model).slice(1).flat();

function find(parts: WorkflowEntry[][], id: string) {
  for (const [s, part] of parts.entries()) {
    const i = part.findIndex((e) => e.id === id);
    if (i >= 0) return { s, i };
  }
  return null;
}

/** Chain links (source → target) between top-level nodes, Input and Output included. */
export function chainLinks(model: CanvasModel): Array<{ source: string; target: string }> {
  const parts = segments(model);
  const links: Array<{ source: string; target: string }> = [];
  const main = parts[0]!;
  let prev = CANVAS_INPUT;
  for (const e of main) {
    links.push({ source: prev, target: e.id });
    prev = e.id;
  }
  links.push({ source: prev, target: CANVAS_OUTPUT });
  for (const part of parts.slice(1))
    for (let i = 1; i < part.length; i++)
      links.push({ source: part[i - 1]!.id, target: part[i]!.id });
  return links;
}

export type CanvasResult = { model: CanvasModel } | { error: string };

/**
 * Links `source` → `target`: `target` and the steps after it in its segment move to right after
 * `source`, and whatever followed `source` continues after them. Linking from a step that is not
 * connected puts it (and its segment) in front of `target` instead.
 */
export function connect(model: CanvasModel, source: string, target: string): CanvasResult {
  if (target === CANVAS_INPUT || source === CANVAS_OUTPUT || source === target)
    return { error: 'Links go from a step to a later one' };
  const parts = segments(model);
  if (target === CANVAS_OUTPUT) {
    // Linking a loose segment's end to Output appends it to the main chain.
    const from = find(parts, source);
    if (!from || from.s === 0) return { model };
    const part = parts[from.s]!;
    parts[0] = [...parts[0]!, ...part];
    parts.splice(from.s, 1);
    return { model: fromSegments(parts) };
  }
  const to = find(parts, target);
  if (!to) return { error: 'Unknown step' };

  if (source !== CANVAS_INPUT) {
    const from = find(parts, source);
    if (!from) return { error: 'Unknown step' };
    if (from.s === to.s && from.i >= to.i)
      return { error: 'Steps run left to right — use a Loop step to repeat' };
    if (from.s !== 0 && to.s === 0) {
      // A loose segment linked into the workflow: it runs before `target`.
      const run = parts[from.s]!.slice(0, from.i + 1);
      const rest = parts[from.s]!.slice(from.i + 1);
      parts[0] = [...parts[0]!.slice(0, to.i), ...run, ...parts[0]!.slice(to.i)];
      parts[from.s] = rest;
      return { model: fromSegments(parts) };
    }
  }

  const moving = parts[to.s]!.slice(to.i);
  parts[to.s] = parts[to.s]!.slice(0, to.i);
  const at = source === CANVAS_INPUT ? { s: 0, i: -1 } : find(parts, source)!;
  const host = parts[at.s]!;
  parts[at.s] = [...host.slice(0, at.i + 1), ...moving, ...host.slice(at.i + 1)];
  return { model: fromSegments(parts) };
}

/** Removes the link `source` → `target`: `target` and what follows become unconnected. */
export function disconnect(model: CanvasModel, source: string, target: string): CanvasModel {
  if (target === CANVAS_OUTPUT) return model;
  const parts = segments(model);
  const to = find(parts, target);
  if (!to) return model;
  if (source === CANVAS_INPUT && to.s === 0 && to.i === 0) {
    return fromSegments([[], parts[0]!, ...parts.slice(1)]);
  }
  if (to.i === 0) return model;
  const part = parts[to.s]!;
  parts.splice(to.s, 1, part.slice(0, to.i), part.slice(to.i));
  return fromSegments(parts);
}

/** Deletes a top-level entry; its neighbours are linked to each other. */
export function removeEntry(model: CanvasModel, id: string): CanvasModel {
  const parts = segments(model).map((p) => p.filter((e) => e.id !== id));
  return fromSegments(parts);
}

/** Puts a new entry right after `source` (Input, a step, or nothing → a new loose node). */
export function insertAfter(
  model: CanvasModel,
  source: string | null,
  entry: WorkflowEntry,
): CanvasModel {
  const parts = segments(model);
  if (source === null) return fromSegments([...parts, [entry]]);
  if (source === CANVAS_INPUT) {
    parts[0] = [entry, ...parts[0]!];
    return fromSegments(parts);
  }
  const at = find(parts, source);
  if (!at) return fromSegments([...parts, [entry]]);
  const host = parts[at.s]!;
  parts[at.s] = [...host.slice(0, at.i + 1), entry, ...host.slice(at.i + 1)];
  return fromSegments(parts);
}

/** Appends to the end of the main chain. */
export function append(model: CanvasModel, entry: WorkflowEntry): CanvasModel {
  const parts = segments(model);
  parts[0] = [...parts[0]!, entry];
  return fromSegments(parts);
}

/** Replaces the graph (e.g. after an inspector edit), keeping breaks whose ids still exist. */
export function withGraph(model: CanvasModel, graph: WorkflowEntry[]): CanvasModel {
  // Ids renamed in place keep their break (same index, new id).
  const renamed = new Map<string, string>();
  if (graph.length === model.graph.length)
    model.graph.forEach((e, i) => {
      if (graph[i] && graph[i]!.id !== e.id) renamed.set(e.id, graph[i]!.id);
    });
  const ids = new Set(graph.map((e) => e.id));
  const breaks = model.breaks.map((b) => renamed.get(b) ?? b).filter((b) => ids.has(b));
  return { graph, breaks };
}

/** The steps inside a container entry, with whether one more can be removed. */
export function containerChildren(entry: WorkflowEntry): WorkflowContainerStep[] {
  switch (entry.type) {
    case 'parallel':
      return entry.steps;
    case 'conditional':
      return [...entry.branches.map((b) => b.step), ...(entry.fallback ? [entry.fallback] : [])];
    case 'loop':
    case 'foreach':
      return [entry.step];
    default:
      return [];
  }
}

/** Removes a step inside a container, if the container allows it. */
export function removeChild(
  entry: WorkflowEntry,
  childId: string,
): WorkflowEntry | { error: string } {
  switch (entry.type) {
    case 'parallel':
      if (entry.steps.length <= 2) return { error: 'A parallel step needs at least two steps' };
      return { ...entry, steps: entry.steps.filter((s) => s.id !== childId) };
    case 'conditional': {
      if (entry.fallback?.id === childId) {
        const { fallback: _drop, ...rest } = entry;
        return rest;
      }
      if (entry.branches.length <= 1) return { error: 'A branch step needs at least one branch' };
      return { ...entry, branches: entry.branches.filter((b) => b.step.id !== childId) };
    }
    case 'loop':
    case 'foreach':
      return {
        error: `Change the step a ${entry.type === 'loop' ? 'loop' : 'for each'} runs instead of deleting it`,
      };
    default:
      return { error: 'Not a container' };
  }
}

/** A copy of an entry with fresh ids (children too). */
export function duplicateEntry(entry: WorkflowEntry, taken: Set<string>): WorkflowEntry {
  const fresh = (id: string) => {
    const base = id.replace(/_copy(_\d+)?$/, '');
    let next = `${base}_copy`.slice(0, 40);
    for (let n = 2; taken.has(next); n++) next = `${base}_copy_${n}`.slice(0, 40);
    taken.add(next);
    return next;
  };
  const leaf = <T extends { id: string; name: string }>(s: T): T => ({ ...s, id: fresh(s.id) });
  const copy = { ...entry, id: fresh(entry.id), name: `${entry.name} (copy)`.slice(0, 100) };
  switch (copy.type) {
    case 'parallel':
      return { ...copy, steps: copy.steps.map(leaf) };
    case 'conditional':
      return {
        ...copy,
        branches: copy.branches.map((b) => ({ ...b, step: leaf(b.step) })),
        ...(copy.fallback ? { fallback: leaf(copy.fallback) } : {}),
      };
    case 'loop':
    case 'foreach':
      return { ...copy, step: leaf(copy.step) };
    default:
      return copy as WorkflowEntry;
  }
}
